// The sheet, and the furniture every sheet is built out of.
//
// One shape, ported from the approved prototype: a scrim over the pane, a panel
// that rises from the bottom on a phone and centres on a wide screen, a
// scrolling body and a footer that does not scroll with it. Everything the
// create-and-edit flow shows lives in one of these.
//
// Modality is real rather than drawn. `App.tsx` marks the day screen, the
// sticky bar and the + button `inert` while a sheet is open, so the browser
// itself keeps focus and the screen reader inside the dialog - which is what a
// hand-written focus trap is usually a worse version of.

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { isSignedOut, messageFor } from '../api/client.ts';
import { Icon } from '../day/Icon.tsx';
import { useMode } from '../shell/theme.ts';
import { hueIn } from './edit-model.ts';

interface SheetProps {
  /** Names the dialog. Not drawn - the body carries its own heading. */
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}

export function Sheet({ title, onClose, children, footer }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Somewhere inside, always. An `autoFocus` field takes it first; when there
    // is none, the panel itself does, so the first Tab lands on the sheet's own
    // controls rather than back at the top of a document nothing can reach.
    //
    // This is the way IN only. The way out - focus back to the control that
    // opened the flow - is `useEditor`'s, because it belongs to the whole run
    // of sheets rather than to any one panel: this one may be the wizard's
    // second step, or the form that replaced a "Loading…" panel, and the button
    // that opened it was in the panel before it and is already gone. The reason
    // it cannot be read from an effect at all is written there.
    const node = panel.current;
    if (node !== null && !node.contains(document.activeElement)) node.focus();
  }, []);

  return (
    <>
      {/* The scrim closes the sheet, as it does in the prototype. It carries no
          role and no label: it is a duplicate of Cancel for a pointer, and
          announcing it would put a second, wordless way out in the reading
          order. Escape is the keyboard's version, below. */}
      <div className="sheetveil" onClick={onClose} />
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panel}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="sheet-body">{children}</div>
        <div className="sheet-foot">{footer}</div>
      </div>
    </>
  );
}

export type Loaded<T> =
  { status: 'loading' } | { status: 'ready'; value: T } | { status: 'failed'; message: string };

/**
 * One read, made when a sheet opens.
 *
 * A 401 here raises the signed-out screen rather than filling the sheet with a
 * reason, exactly as every other request in this client does: it is a state of
 * the session and not of this read, and the shell around the sheet is about to
 * be replaced anyway.
 *
 * It lives with the sheet furniture rather than beside its first caller because
 * it is the shape any sheet that has to read before it can draw takes - the
 * item sheet reads the row it is about - and a second copy of a read-once
 * effect is two places for the same subtlety about stale closures to be got
 * wrong.
 */
export function useOnce<T>(
  subject: string,
  load: (subject: string) => Promise<T>,
  onSignedOut: () => void,
): Loaded<T> {
  const [state, setState] = useState<Loaded<T>>({ status: 'loading' });

  // What the read is FOR is the dependency; how it is made is not. `load` and
  // `onSignedOut` are fresh closures on every render, and depending on either
  // would re-read the row on every keystroke in the form below - so they are
  // held in a ref that the effect reads at the moment it fires.
  const latest = useRef({ load, onSignedOut });
  latest.current = { load, onSignedOut };

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const value = await latest.current.load(subject);
        if (live) setState({ status: 'ready', value });
      } catch (error) {
        if (isSignedOut(error)) {
          latest.current.onSignedOut();
          return;
        }
        if (live) setState({ status: 'failed', message: messageFor(error) });
      }
    })();
    return () => {
      live = false;
    };
  }, [subject]);

  return state;
}

/** A sheet with its read still in flight. */
export function Waiting({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <Sheet
      title={title}
      onClose={onClose}
      footer={
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
      }
    >
      <p className="sheetwait" role="status">
        Loading…
      </p>
    </Sheet>
  );
}

/** A sheet whose read did not arrive, saying so where the form would be. */
export function Stuck({
  title,
  message,
  onClose,
}: {
  title: string;
  message: string;
  onClose: () => void;
}) {
  return (
    <Sheet
      title={title}
      onClose={onClose}
      footer={
        <button className="btn primary" onClick={onClose}>
          Close
        </button>
      }
    >
      <h3>That did not open.</h3>
      <p className="sub">{message}</p>
    </Sheet>
  );
}

/** The step counter over a wizard's heading - 'Step 2 of 3'. */
export function StepLabel({ children }: { children: ReactNode }) {
  return <div className="steplabel">{children}</div>;
}

/**
 * A quiet block of explanation. Used where two ideas are easiest to confuse and
 * nowhere else, which is the prototype's own rule for it: a notice on every
 * screen is a screen nobody reads.
 */
export function Notice({ children }: { children: ReactNode }) {
  return <div className="notice">{children}</div>;
}

let nextFieldId = 0;

/** A labelled control. The label is a real `<label for>` when it can be. */
export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  /** Given the id to put on the control, so the label points at it. */
  children: (id: string) => ReactNode;
  hint?: string;
}) {
  const id = useRef<string>('');
  if (id.current === '') id.current = `field-${(nextFieldId += 1)}`;

  return (
    <div className="field">
      <label htmlFor={id.current}>{label}</label>
      {children(id.current)}
      {hint !== undefined && <p className="fieldhint">{hint}</p>}
    </div>
  );
}

/**
 * A group of controls under one label. `role="group"` with the label as its
 * name, because a `<label for>` can only point at one control and a row of
 * chips is not one - a screen reader would otherwise read seven unrelated
 * buttons with no idea what they choose between.
 */
export function FieldGroup({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <div className="field">
      <span className="fieldlabel">{label}</span>
      <div role="group" aria-label={label}>
        {children}
      </div>
      {hint !== undefined && <p className="fieldhint">{hint}</p>}
    </div>
  );
}

interface ChoiceProps<T extends string | number> {
  options: readonly { key: T; label: string; name?: string }[];
  chosen: readonly T[];
  onChoose: (key: T) => void;
}

/**
 * Pill buttons where one or several are chosen. `aria-pressed` rather than
 * radio inputs: the pressed pill is drawn, and a native radio brings a control
 * that cannot be styled into one.
 */
export function Chips<T extends string | number>({ options, chosen, onChoose }: ChoiceProps<T>) {
  return (
    <div className="chips">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          aria-pressed={chosen.includes(option.key)}
          onClick={() => onChoose(option.key)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** The seven-square weekday picker. Sunday first, as `weekdayIndex()` counts. */
export function DayPicker({ options, chosen, onChoose }: ChoiceProps<number>) {
  return (
    <div className="dows">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          aria-pressed={chosen.includes(option.key)}
          aria-label={option.name ?? option.label}
          onClick={() => onChoose(option.key)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** The colour swatches: one button per hue, drawn as the card is drawn. */
export function Swatches({
  options,
  chosen,
  onChoose,
}: {
  options: readonly { name: string; hex: string }[];
  chosen: string;
  onChoose: (hex: string) => void;
}) {
  // The swatch is PAINTED in the mode on screen and CHOSEN as itself: `--c`
  // goes through `hueIn`, while `option.hex` - what is compared and what is
  // sent - stays the stored light value. A picker that saved what it painted
  // would write a dark hex into the database the first time anyone used it.
  const mode = useMode();
  return (
    <div className="swatches">
      {options.map((option) => (
        <button
          key={option.hex}
          type="button"
          style={{ '--c': hueIn(option.hex, mode) } as CSSProperties}
          aria-pressed={option.hex === chosen}
          aria-label={option.name}
          onClick={() => onChoose(option.hex)}
        />
      ))}
    </div>
  );
}

export function IconGrid({
  options,
  chosen,
  onChoose,
}: {
  options: readonly string[];
  chosen: string;
  onChoose: (symbol: string) => void;
}) {
  return (
    <div className="icongrid">
      {options.map((name) => (
        <button
          key={name}
          type="button"
          aria-pressed={name === chosen}
          aria-label={name}
          onClick={() => onChoose(name)}
        >
          <Icon name={name} />
        </button>
      ))}
    </div>
  );
}
